<template>
  <div
    ref="chart"
    class="echarts-line-chart"
    role="img"
    :aria-label="ariaLabel"
  />
</template>

<script>
import echarts, { chartAnimationDuration } from './echarts-core'

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const AXIS_CURRENCY_FORMATTER = new Intl.NumberFormat('en-CA', {
  style: 'currency',
  currency: 'CAD',
  maximumFractionDigits: 0
})

export default {
  name: 'EChartsLineChart',
  props: {
    labels: {
      type: Array,
      required: true
    },
    values: {
      type: Array,
      required: true
    },
    seriesName: {
      type: String,
      default: 'Revenue'
    },
    isDark: {
      type: Boolean,
      default: false
    },
    ariaLabel: {
      type: String,
      default: 'Line chart'
    },
    color: {
      type: String,
      default: '#5a78ff'
    },
    areaColor: {
      type: String,
      default: 'rgba(90, 120, 255, 0.12)'
    },
    valueFormatter: {
      type: Function,
      default: value => CURRENCY_FORMATTER.format(Number(value) || 0)
    },
    axisValueFormatter: {
      type: Function,
      default: value => AXIS_CURRENCY_FORMATTER.format(Number(value) || 0)
    }
  },
  data() {
    return {
      chart: null,
      resizeObserver: null
    }
  },
  mounted() {
    this.$nextTick(this.initChart)
  },
  beforeDestroy() {
    this.teardownResize()

    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  watch: {
    labels: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    values: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    isDark() {
      this.updateChart()
    }
  },
  methods: {
    initChart() {
      if (!this.$refs.chart || this.chart) return

      this.chart = echarts.init(this.$refs.chart, null, {
        renderer: 'canvas'
      })
      this.bindResize()
      this.updateChart()
    },
    updateChart() {
      if (!this.chart) {
        this.$nextTick(this.initChart)
        return
      }

      this.chart.setOption(this.buildOptions(), true)
      this.refresh()
    },
    refresh() {
      this.$nextTick(() => {
        if (this.chart) {
          this.chart.resize()
        }
      })
    },
    bindResize() {
      window.addEventListener('resize', this.refresh)

      if (typeof ResizeObserver !== 'undefined' && this.$refs.chart) {
        this.resizeObserver = new ResizeObserver(this.refresh)
        this.resizeObserver.observe(this.$refs.chart)
      }
    },
    teardownResize() {
      window.removeEventListener('resize', this.refresh)

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    },
    formatValue(value) {
      return this.valueFormatter(value)
    },
    formatAxisValue(value) {
      return this.axisValueFormatter(value)
    },
    buildOptions() {
      const textColor = this.isDark ? 'rgba(255,255,255,0.72)' : 'rgba(0,0,0,0.58)'
      const gridColor = this.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
      const tooltipBackground = this.isDark ? 'rgba(35,35,35,0.96)' : 'rgba(20,20,20,0.88)'

      return {
        animationDuration: chartAnimationDuration(),
        color: [this.color],
        grid: {
          top: 34,
          right: 20,
          bottom: 34,
          left: 16,
          containLabel: true
        },
        legend: {
          top: 0,
          icon: 'rect',
          itemWidth: 28,
          itemHeight: 8,
          textStyle: {
            color: textColor,
            fontSize: 11
          },
          data: [this.seriesName]
        },
        tooltip: {
          trigger: 'axis',
          renderMode: 'richText',
          confine: true,
          backgroundColor: tooltipBackground,
          borderWidth: 0,
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          axisPointer: {
            type: 'line',
            lineStyle: {
              color: this.color,
              opacity: 0.32
            }
          },
          formatter: params => {
            const point = Array.isArray(params) ? params[0] : params
            if (!point) return ''
            return [
              point.axisValue,
              `${this.seriesName}: ${this.formatValue(point.data)}`
            ].join('\n')
          }
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: this.labels,
          axisTick: {
            show: false
          },
          axisLine: {
            lineStyle: {
              color: gridColor
            }
          },
          axisLabel: {
            color: textColor,
            hideOverlap: true,
            margin: 12
          },
          splitLine: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          min: value => Math.min(0, value.min),
          axisLabel: {
            color: textColor,
            formatter: value => this.formatAxisValue(value),
            margin: 12
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: gridColor
            }
          }
        },
        series: [
          {
            name: this.seriesName,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 7,
            showSymbol: this.labels.length <= 45,
            data: this.values,
            lineStyle: {
              color: this.color,
              width: 2.25
            },
            itemStyle: {
              color: this.color,
              borderColor: '#fff',
              borderWidth: 1.5
            },
            areaStyle: {
              color: this.areaColor
            },
            emphasis: {
              focus: 'series'
            }
          }
        ]
      }
    }
  }
}
</script>

<style scoped lang="scss">
.echarts-line-chart {
  width: 100%;
  height: 100%;
  min-height: 320px;
}
</style>
